/**
 * Line Count Tool
 *
 * Counts total lines, blank lines, non-blank lines, and code lines (non-blank, non-comment).
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const LineCountTool: ITransformerTool = {
  id: 'line-count',
  name: 'Line Count',
  description: 'Count total, blank, non-blank, and code lines',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['line', 'count', 'blank', 'code', 'text', 'statistics'],

  seo: {
    slug: 'line-counter',
    pageTitle: 'Line Counter - Free Online Tool | SWE DevTools',
    metaDescription: 'Count total lines, blank lines, non-blank lines, and code lines in your text. Detects common comment styles. Fast, private, runs in your browser.',
    keywords: ['line counter', 'line count online', 'count lines in text', 'code line counter', 'blank line counter'],
    relatedTools: ['word-count', 'character-count', 'remove-empty-lines'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    if (input === '') {
      return [
        'Total Lines:     0',
        'Blank Lines:     0',
        'Non-Blank Lines: 0',
        'Code Lines:      0',
      ].join('\n');
    }

    const lines = input.split('\n');
    const total = lines.length;
    let blank = 0;
    let comment = 0;

    // Common single-line comment patterns
    const commentPatterns = /^\s*(\/\/|#|--|;|%|\/\*|\*|<!--)/;

    for (const line of lines) {
      if (line.trim() === '') {
        blank++;
      } else if (commentPatterns.test(line)) {
        comment++;
      }
    }

    const nonBlank = total - blank;
    const code = total - blank - comment;

    return [
      `Total Lines:     ${total.toLocaleString()}`,
      `Blank Lines:     ${blank.toLocaleString()}`,
      `Non-Blank Lines: ${nonBlank.toLocaleString()}`,
      `Code Lines:      ${code.toLocaleString()}`,
      `Comment Lines:   ${comment.toLocaleString()}`,
    ].join('\n');
  },
};
